/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
  DistinctPatchFailuresFilterByFields,
  DistinctPatchFailuresSortFields,
  FilterComparisonOperator,
  SortOrder,
  useGetDistinctPatchFailuresQuery,
} from "~/generated/graphql";
import { useCustomer } from "~/context";
import {
  FAILURES_LIMIT,
  LOOKBACK_MONTHS,
} from "~/views/dashboard/components/DistinctUpdateFailures/consts";

export function useGetDistinctPatchFailures() {
  const { customer } = useCustomer();

  const startTime = useMemo(
    () => dayjs().subtract(LOOKBACK_MONTHS, "month").toISOString(),
    []
  );

  const { data, loading, error, refetch } = useGetDistinctPatchFailuresQuery({
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    variables: {
      limit: FAILURES_LIMIT,
      sort: {
        field: DistinctPatchFailuresSortFields.DistinctFailureCount,
        order: SortOrder.Desc,
      },
      filter: {
        filterBy: [
          {
            field: DistinctPatchFailuresFilterByFields.StartTime,
            operator: FilterComparisonOperator.Ge,
            values: [startTime],
          },
        ],
      },
    },
  });

  useEffect(() => {
    refetch();
  }, [customer, refetch]);

  return {
    failures: data?.distinctPatchFailures.result ?? [],
    loading: loading,
    error,
  } as const;
}
